import { cn } from '@/client/utils';
import { ArrowUpToLineIcon } from 'lucide-react';
import { type RefObject, useEffect, useMemo } from 'react';
import useLocation from 'react-use/lib/useLocation';
import useMedia from 'react-use/lib/useMedia';
import useScroll from 'react-use/lib/useScroll';
import useWindowScroll from 'react-use/lib/useWindowScroll';

type BackTopProps = {
  className?: string;
  showHeight?: number;
  minStep?: number;
  maxCount?: number;
  interval?: number;
  deviation?: number;
  parent?: RefObject<HTMLElement>;
};

export const ScrollTop = ({
  showHeight = 80,
  minStep = 80,
  maxCount = 5,
  interval = 60,
  deviation = 50,
  parent,
  ...attrs
}: BackTopProps) => {
  let history = 0;
  let top = 0;
  let start = Date.now();
  const isDesktop = useMedia('(min-width: 768px)');
  const scrollElement = parent ? parent.current : window.document.documentElement;
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const scroll = parent ? useScroll(parent) : useWindowScroll();
  const isShow = useMemo(() => scroll.y > showHeight, [scroll.y, showHeight]);
  const location = useLocation();

  const scrollToTop = () => {
    if (Date.now() - start >= interval && scrollElement && scroll?.y) {
      if (Math.abs(scrollElement.scrollTop - history) <= deviation) {
        if (scrollElement.scrollTop > 0) {
          const y = scrollElement.scrollTop - Math.min(scrollElement.scrollTop, top);
          scrollElement.scroll(scrollElement.scrollLeft, y);
          history = scrollElement.scrollTop;
          start = Date.now();
          window.requestAnimationFrame(scrollToTop);
        }
      }
    } else window.requestAnimationFrame(scrollToTop);
  };

  const gotTop = () => {
    if (!scroll?.y) return;
    history = scrollElement?.scrollTop || 0;
    top = Math.max(Math.floor(history / maxCount), minStep);
    scrollToTop();
  };
  useEffect(() => {
    if (location && scrollElement) {
      scrollElement.scrollTop = 0;
    }
  }, [location, scrollElement]);

  if (isDesktop) return null;
  return (
    <aside
      className={cn(
        'pos-fixed bottom-0 end-4 z-50',
        'transition-all duration-200',
        'h-10 w-10 cursor-pointer select-none flex items-center justify-center bg-card rounded-full border  border-foreground/4 shadow-xl outline-none md:end-8',
        'hover:scale-105',
        attrs.className,
        isShow ? '-translate-y-32 opacity-90 md:-translate-y-8' : 'translate-y-32 opacity-0 md:translate-y-8'
      )}
      onClick={gotTop}
      {...attrs}
    >
      <ArrowUpToLineIcon className="size-4 fill-current" />
    </aside>
  );
};
